/**
 * ⚠️ FORKED COMPONENT — DO NOT MERGE BACK ⚠️
 *
 * LR DIRECT — QUOTE detail view (post_type='quote').
 * Pixel-clone of the approved quote mockup. QUOTES ONLY —
 * the bid detail layout in DirectLoadDetailPanel is untouched.
 *
 * Presentational only: every value arrives via props from
 * DirectLoadDetailPanel, which still owns all submit logic.
 */

import { useEffect, useMemo, useState } from "react";
import { format, differenceInSeconds } from "date-fns";
import {
  Zap, ShieldCheck, Upload, Eye, FileText, Award, MapPin, Flag,
  Check, CheckCircle2, Send, Loader2, BarChart3,
  Bell, Clock, Lock, AlertTriangle, XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { LtlBadge, isLtlLoad } from "./LtlBadge";
import { cn } from "@/lib/utils";
import { formatEquipmentType } from "@/lib/equipmentTypes";
import type { DirectLoad } from "./DirectResultsTable";
import { useDirectShipperCredit } from "@/hooks/useDirectShipperCredit";

export interface QuoteStop {
  stop_number: number;
  stop_type: string;
  city: string;
  state: string;
  scheduled_date: string;
}

export interface DirectQuoteDetailViewProps {
  load: DirectLoad;
  stops: QuoteStop[];
  stopsLoading: boolean;
  miles: number | null;
  /** Company name of the requesting shipper, or null when it must stay hidden. */
  requestedBy: string | null;
  /** Live countdown to the quote deadline, e.g. "1d 14h 45m". Null = expired/none. */
  deadlineCountdown: string | null;
  deadlinePassed: boolean;
  /* Quote form (owned by the parent) */
  quoteLoading: boolean;
  existingQuote: { rate: number; capacity_note: string | null; status: string; created_at: string; withdraw_reason?: string | null } | null;
  /** Opens the retract-with-reason prompt (parent owns the dialog + RPC). */
  onRetract?: () => void;
  retracting?: boolean;
  rate: string;
  onRateChange: (v: string) => void;
  notes: string;
  onNotesChange: (v: string) => void;
  submitting: boolean;
  canSubmit: boolean;
  onSubmit: () => void;
  /** Tier / auth gate element rendered in place of the form when access is missing. */
  gate?: React.ReactNode;
  /** Aggregate market insights. Null fields render as an empty state. */
  marketInsights: { low: number | null; avg: number | null; high: number | null } | null;
  /** Count of brokers notified for this quote request, when known. */
  notifiedCount: number | null;
  /** True when the viewer is the shipper who owns this quote request. */
  viewerIsShipper?: boolean;
}

const money = (n: number | null | undefined) =>
  n == null ? "—" : `$${Math.round(Number(n)).toLocaleString()}`;

const CARD = "rounded-xl border border-[#E5E9EC] bg-white";
const LABEL = "text-[10px] font-semibold uppercase tracking-[0.08em] text-[#8A9299]";

/* ─── Shipper credit rating (reference data — informational only) ─── */
function CreditRating({ loadId }: { loadId: string }) {
  const { rating } = useDirectShipperCredit(loadId);
  const score = rating.score;
  const tint =
    score >= 90 ? "bg-[rgba(220,252,231,0.15)]" : score >= 70 ? "bg-[rgba(254,249,195,0.15)]" : "bg-[rgba(254,226,226,0.15)]";
  return (
    <div className={cn(CARD, "p-4", tint)}>
      <div className="mb-3 flex items-center gap-1.5">
        <ShieldCheck className="h-3.5 w-3.5 text-[#02857E]" />
        <span className={LABEL}>Shipper credit rating</span>
      </div>
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <div className="h-2.5 w-full rounded-full bg-gradient-to-r from-red-500 via-amber-400 to-[#02857E]" />
          <div
            className="absolute top-1/2 h-3.5 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-black shadow-sm"
            style={{ left: `${Math.min(100, Math.max(0, score))}%` }}
          />
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold leading-none text-[#111827]">{rating.grade} / {rating.score}</div>
          <div className="mt-0.5 text-[11px] font-semibold text-[#02857E]">{rating.label}</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Avg Days to Pay" value={rating.daysToPay} />
        <Stat label="Payment Trend" value={rating.paymentTrend} valueClass="text-[#02857E]" />
        <Stat label="Exposure" value={rating.recommendedExposure} />
        <Stat label="Reported Payments" value={rating.reportedPayments} />
      </div>
      <p className="mt-3 text-[10px] leading-tight text-[#9AA1A8]">
        For reference only. Not a guarantee of payment. Brokers should conduct their own credit
        verification before extending credit. LaneRunner is not responsible for credit decisions.
      </p>
    </div>
  );
}

function Stat({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[9px] font-medium uppercase leading-tight tracking-wider text-[#8A9299]">{label}</div>
      <div className={cn("mt-1 text-[13px] font-bold text-[#111827]", valueClass)}>{value}</div>
    </div>
  );
}

const STEPS = [
  { n: 1, label: "Submit Rate", icon: Upload },
  { n: 2, label: "Shipper Reviews", icon: Eye },
  { n: 3, label: "Load Created", icon: FileText },
  { n: 4, label: "Bid to Win", icon: Award },
];

export default function DirectQuoteDetailView({
  load, stops, stopsLoading, miles, requestedBy,
  deadlineCountdown, deadlinePassed,
  quoteLoading, existingQuote, onRetract, retracting, rate, onRateChange, notes, onNotesChange,
  submitting, canSubmit, onSubmit, gate,
  marketInsights, notifiedCount, viewerIsShipper = false,
}: DirectQuoteDetailViewProps) {
  // UI-only LTL awareness speed bump (never persisted, never part of the quote payload)
  const [ltlAck, setLtlAck] = useState(false);
  const sorted = [...stops].sort((a, b) => (a.stop_number ?? 0) - (b.stop_number ?? 0));
  const pickups = sorted.filter((s) => s.stop_type === "pickup");
  const drops = sorted.filter((s) => s.stop_type !== "pickup");
  const pickCount = pickups.length || 1;
  const dropCount = drops.length || 1;

  const postedAt = load.posted_at ?? null;
  const requirements = (load.handling_requirements ?? []).filter(Boolean);
  const loadSize = load.load_type ? load.load_type.toUpperCase() : "—";

  /* ── Quote deadline urgency (same thresholds as bid countdown) ── */
  const [deadlineLevel, setDeadlineLevel] = useState<"normal" | "warn" | "critical">("normal");
  useEffect(() => {
    if (!load.bid_deadline) {
      setDeadlineLevel("normal");
      return;
    }
    const deadline = new Date(load.bid_deadline);
    const tick = () => {
      const totalSecs = differenceInSeconds(deadline, new Date());
      setDeadlineLevel(totalSecs <= 3600 ? "critical" : totalSecs <= 6 * 3600 ? "warn" : "normal");
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [load.bid_deadline]);

  const quoteDeadlineTone =
    deadlineLevel === "critical"
      ? {
          box: "bg-gradient-to-br from-red-700 via-red-800 to-red-900",
          label: "text-red-200",
          value: "text-white",
          sub: "text-red-200",
        }
      : deadlineLevel === "warn"
        ? {
            box: "bg-gradient-to-br from-amber-500 via-amber-600 to-amber-700",
            label: "text-amber-100",
            value: "text-white",
            sub: "text-amber-100",
          }
        : {
            box: "bg-gradient-to-br from-[#0B8A80] via-[#046D66] to-[#02514D]",
            label: "text-teal-100",
            value: "text-white",
            sub: "text-teal-200",
          };

  const [d, hm] = (() => {
    if (!deadlineCountdown) return [null, null] as const;
    const m = deadlineCountdown.match(/^(\d+d)\s*(.*)$/);
    return m ? [m[1], m[2]] : [null, deadlineCountdown];
  })();

  return (
    <div className="border-t border-[#E5E9EC] bg-[#F7F9FA] p-3 md:p-4">
      {isLtlLoad(load.load_type) && (
        <div className="mb-3 flex items-start gap-2.5 rounded-md border border-amber-500 bg-amber-50 p-2.5">
          <LtlBadge size="lg" label="LTL / Partial" className="mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-bold text-amber-900">This is an LTL / partial shipment</p>
            <p className="text-xs leading-snug text-amber-900/80">
              Not a full truckload move — quote LTL / partial pricing, not full truckload pricing.
            </p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* ══════════ LEFT ══════════ */}
        <div className="flex min-w-0 flex-col gap-4">
          {/* Header */}
          <div className={cn(CARD, "p-4 md:p-5")}>
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div className="min-w-0">
                <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <span className="rounded-full bg-[#F3E8FF] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#7E22CE]">
                    Quote Request
                  </span>
                  {isLtlLoad(load.load_type) && <LtlBadge size="lg" label="LTL / Partial" />}
                  <span className="text-xs font-semibold text-[#6B7280]">
                    {(load as any).load_number ?? load.reference_number ?? "—"}
                  </span>
                </div>
                <h2 className="truncate text-2xl font-bold text-[#111827] md:text-[28px]">
                  {load.origin_city}, {load.origin_state} <span className="font-normal text-[#6B7280]">→</span>{" "}
                  {load.destination_city}, {load.destination_state}
                </h2>
                <div className="mt-1.5 flex items-center gap-2 text-[13px] text-[#6B7280]">
                  <span className="rounded bg-[#F1F5F5] px-1.5 py-0.5 text-[10px] font-bold text-[#4B5563]">
                    {formatEquipmentType(load.equipment_type).slice(0, 2)}
                  </span>
                  <span>{formatEquipmentType(load.equipment_type)}</span>
                  <span>•</span>
                  <span>{loadSize}</span>
                  <span>•</span>
                  <span>
                    {stopsLoading ? "…" : `${pickCount} Pick${pickCount === 1 ? "" : "s"} / ${dropCount} Drop${dropCount === 1 ? "" : "s"}`}
                  </span>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:shrink-0 md:items-start gap-4 md:gap-6 xl:gap-8">
                <div className="border-l border-[#E5E9EC] pl-5">
                  <div className={LABEL}>Requested by</div>
                  <div className="mt-1.5 text-sm font-semibold text-[#111827]">
                    {requestedBy ?? "Hidden until awarded"}
                  </div>
                </div>
                <div className="border-l border-[#E5E9EC] pl-5">
                  <div className={LABEL}>Requested</div>
                  <div className="mt-1.5 text-sm font-semibold text-[#111827]">
                    {postedAt ? format(new Date(postedAt), "MMM d, yyyy h:mm a") : "—"}
                  </div>
                </div>
                <div className={cn("w-full md:w-auto rounded-xl border-l-4 p-4 pl-5", quoteDeadlineTone.box, deadlineLevel === "critical" ? "border-red-300" : deadlineLevel === "warn" ? "border-amber-200" : "border-[#40E0D0]")}>
                  <div className={cn("text-[10px] font-semibold uppercase tracking-[0.08em]", quoteDeadlineTone.label)}>
                    {deadlinePassed ? "Quote deadline" : deadlineLevel === "critical" ? "Closing now" : deadlineLevel === "warn" ? "Closing soon" : "Quote deadline"}
                  </div>
                  <div className={cn("mt-1 text-[22px] font-bold leading-none tabular-nums", quoteDeadlineTone.value)}>
                    {deadlineCountdown ? (
                      <>
                        {d && <>{d.replace("d", "")}<span className="text-base font-semibold">d </span></>}
                        {hm?.split(" ").map((part, i) => (
                          <span key={i}>
                            {part.replace(/[hm]/, "")}
                            <span className="text-base font-semibold">{part.slice(-1)} </span>
                          </span>
                        ))}
                      </>
                    ) : (
                      <span className="text-base font-semibold">
                        {deadlinePassed ? "Closed" : "—"}
                      </span>
                    )}
                  </div>
                  <div className={cn("mt-1 text-[11px]", quoteDeadlineTone.sub)}>
                    {load.bid_deadline ? format(new Date(load.bid_deadline), "MMM d, yyyy h:mm a") : "No deadline set"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How quoting works + credit rating */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className={cn(CARD, "border-[#F4E7C3] bg-[#FFFBEF] p-4")}>
              <div className="mb-2 flex items-center gap-2">
                <Zap className="h-4 w-4 text-[#E0A80D]" />
                <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#3F3B33]">
                  How quoting works
                </span>
              </div>
              <p className="text-[13px] leading-snug text-[#4B5563]">
                Submit your best all-in rate. If your rate works for the shipper, they'll turn this
                into a live load you can bid on to win the freight.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-1">
                {STEPS.map((s, i) => (
                  <div key={s.n} className="flex items-start gap-2 sm:gap-1">
                    <div className="flex min-w-0 flex-1 flex-row sm:flex-col items-center sm:text-center">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F0F5F4] ring-1 ring-[#DCE5E3]">
                        <s.icon className="h-3.5 w-3.5 text-[#5C6B69]" strokeWidth={1.75} />
                      </div>
                      <span className="ml-2 sm:ml-0 sm:mt-1.5 text-[10px] font-medium leading-tight text-[#5C6B69]">{s.label}</span>
                    </div>
                    {i < STEPS.length - 1 && (
                      <span className="hidden sm:block mt-3.5 text-[#C6CDCB]">→</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <CreditRating loadId={load.id} />
          </div>

          {/* Route & load details */}
          <div className={cn(CARD, "overflow-hidden")}>
            <div className="border-b border-[#E5E9EC] px-4 py-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#4B5563]">
                Route &amp; load details
              </span>
            </div>
            <div className="grid grid-cols-1 gap-6 p-4 md:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr]">
              <StopList
                title="Pickups"
                count={pickups.length}
                icon={<MapPin className="h-3.5 w-3.5 text-white" />}
                iconBg="bg-[#02857E]"
                stops={pickups}
                loading={stopsLoading}
                fallback={{ city: load.origin_city, state: load.origin_state, date: load.pickup_date }}
              />
              <StopList
                title="Drops"
                count={drops.length}
                icon={<Flag className="h-3.5 w-3.5 text-white" />}
                iconBg="bg-[#E0A80D]"
                stops={drops}
                loading={stopsLoading}
                fallback={{ city: load.destination_city, state: load.destination_state, date: load.delivery_date }}
              />
              <div className="divide-y divide-[#EEF1F3] border-l border-[#EEF1F3] pl-0 lg:pl-6">
                <SpecRow label="Equipment" value={formatEquipmentType(load.equipment_type)} />
                <SpecRow label="Temp Range" value="—" />
                <SpecRow label="Weight" value={load.weight_lbs ? `${Number(load.weight_lbs).toLocaleString()} lbs` : "—"} />
                <SpecRow label="Commodity" value={load.commodity || "—"} />
                <SpecRow label="Load Type" value={loadSize} />
                <SpecRow label="Distance" value={miles ? `${miles.toLocaleString()} mi` : "—"} />
                <SpecRow label="References" value={load.reference_number || "Available on request"} />
              </div>
            </div>
          </div>

          {/* Requirements + market insights */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
            <div className={cn(CARD, "p-4")}>
              <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#4B5563]">
                Requirements
              </div>
              {requirements.length === 0 && !load.special_instructions ? (
                <p className="text-[13px] text-[#8A9299]">No special requirements listed.</p>
              ) : (
                <ul className="space-y-2">
                  {requirements.map((r) => (
                    <li key={r} className="flex items-start gap-2 text-[13px] text-[#374151]">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#02857E]" strokeWidth={2.5} />
                      <span>{r}</span>
                    </li>
                  ))}
                  {load.special_instructions && (
                    <li className="flex items-start gap-2 text-[13px] text-[#374151]">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#02857E]" strokeWidth={2.5} />
                      <span>{load.special_instructions}</span>
                    </li>
                  )}
                </ul>
              )}
              {load.shipper_notes_to_brokers && (
                <p className="mt-3 rounded-lg bg-[#F7F9FA] p-2.5 text-[12px] leading-snug text-[#4B5563]">
                  {load.shipper_notes_to_brokers}
                </p>
              )}
            </div>

            <div className={cn(CARD, "p-4")}>
              <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#4B5563]">
                LaneRunner market insights
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <InsightTile label="Low Range" value={money(marketInsights?.low)} bg="bg-[#F0F7F5]" iconClass="text-[#02857E]" />
                <InsightTile label="Avg Market Rate" value={money(marketInsights?.avg)} bg="bg-[#F0F7F5]" iconClass="text-[#02857E]" />
                <InsightTile label="High Range" value={money(marketInsights?.high)} bg="bg-[#FFFBEF]" iconClass="text-[#E0A80D]" />
              </div>
              <div className="mt-3 rounded-lg bg-[#F7F9FA] px-3 py-2.5">
                <p className="text-[11px] text-[#6B7280]">
                  {marketInsights?.avg == null
                    ? "No quotes yet — insights fill in as brokers submit rates."
                    : "Insights update in real time based on accepted market rates."}
                </p>
                {!viewerIsShipper && (
                  <p className="mt-1 flex items-center gap-1 text-[11px] text-[#8A9299]">
                    <Lock className="h-3 w-3" /> Aggregate only — individual quotes and broker
                    identities are never shown.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Confidential footer */}
          <div className="flex flex-wrap items-center gap-3 rounded-xl bg-[#EEF2F3] px-4 py-3">
            <span className="flex items-center gap-1.5 text-[12px] font-semibold text-[#02857E]">
              <ShieldCheck className="h-4 w-4" /> Private &amp; Confidential
            </span>
            <span className="text-[12px] text-[#6B7280]">
              {viewerIsShipper
                ? "Broker quotes are private to you. Brokers cannot see each other's rates."
                : "Your quote is only visible to the shipper after submission. Other brokers cannot see your rate."}
            </span>
          </div>
        </div>

        {/* ══════════ RIGHT ══════════ */}
        <div className="flex flex-col gap-4">
          {/* YOUR QUOTE */}
          <div className="rounded-xl border-2 border-[#02857E] bg-white">
            <div className="flex items-center justify-between border-b border-[#E5E9EC] px-4 py-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#4B5563]">Your quote</span>
              <span
                className={cn(
                  "rounded-full px-2.5 py-1 text-[10px] font-semibold",
                  existingQuote && existingQuote.status !== "withdrawn"
                    ? "bg-[#E6F4F1] text-[#02857E]"
                    : "bg-[#F1F5F5] text-[#6B7280]",
                )}
              >
                {existingQuote
                  ? existingQuote.status === "withdrawn"
                    ? "Retracted"
                    : "Submitted"
                  : "Not Submitted"}
              </span>
            </div>

            <div className="p-4">
              {quoteLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : existingQuote ? (
                <div className="space-y-2">
                  <div className={LABEL}>All-in rate</div>
                  <div
                    className={cn(
                      "text-3xl font-bold tabular-nums",
                      existingQuote.status === "withdrawn" ? "text-[#8A9299] line-through" : "text-[#111827]",
                    )}
                  >
                    {money(existingQuote.rate)}
                  </div>
                  {existingQuote.capacity_note && (
                    <p className="text-[12px] text-[#4B5563]">{existingQuote.capacity_note}</p>
                  )}
                  <p className="text-[11px] text-[#8A9299]">
                    Sent {format(new Date(existingQuote.created_at), "MMM d, h:mm a")}
                  </p>
                  {existingQuote.status === "withdrawn" ? (
                    <>
                      {existingQuote.withdraw_reason && (
                        <p className="text-[11px] text-[#6B7280]">Reason: {existingQuote.withdraw_reason}</p>
                      )}
                      <p className="text-[11px] italic text-[#8A9299]">
                        You retracted this quote. The shipper can see it was retracted and why.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-[11px] italic text-[#8A9299]">
                        Indicative pricing only — the shipper may convert this into a live load.
                      </p>
                      {onRetract && !deadlinePassed && existingQuote.status === "active" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={onRetract}
                          disabled={retracting}
                          className="mt-1 h-8 border-[#E5E9EC] text-[12px] text-[#B42318] hover:bg-[#FEF3F2] hover:text-[#B42318]"
                        >
                          {retracting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <XCircle className="mr-1.5 h-3.5 w-3.5" />}
                          Retract quote
                        </Button>
                      )}
                    </>
                  )}
                </div>
              ) : deadlinePassed ? (
                <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12px] font-semibold text-amber-900">
                  <AlertTriangle className="h-4 w-4 text-amber-600" /> Quote deadline has passed
                </div>
              ) : gate ? (
                gate
              ) : (
                <>
                  {isLtlLoad(load.load_type) && (
                    <div className="mb-3 rounded-md border border-amber-500 bg-amber-50 p-2.5">
                      <div className="flex items-center gap-1.5">
                        <LtlBadge size="default" label="LTL / Partial" />
                        <p className="text-xs font-bold text-amber-900">You are bidding on an LTL load</p>
                      </div>
                      <p className="mt-1 text-[11px] leading-snug text-amber-900/80">
                        Submit LTL / partial pricing, not full truckload pricing.
                      </p>
                    </div>
                  )}
                  <div className={LABEL}>All-in rate</div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-2xl font-bold text-[#111827]">$</span>
                    <Input
                      type="number"
                      inputMode="decimal"
                      placeholder="— — — — —"
                      value={rate}
                      onChange={(e) => onRateChange(e.target.value)}
                      className="h-11 flex-1 border-0 border-b border-[#E5E9EC] bg-transparent px-0 text-2xl font-bold tabular-nums shadow-none focus-visible:ring-0 rounded-none"
                    />
                    <span className="text-[11px] font-semibold text-[#8A9299]">USD</span>
                  </div>
                  {rate && miles && Number(rate) > 0 && (
                    <p className="mt-1 text-[11px] text-[#8A9299]">
                      ${(Number(rate) / miles).toFixed(2)}/mi · {miles.toLocaleString()} mi
                    </p>
                  )}

                  <div className="mt-4">
                    <div className={LABEL}>Rate notes (optional)</div>
                    <Textarea
                      value={notes}
                      onChange={(e) => onNotesChange(e.target.value.slice(0, 200))}
                      placeholder="Add any notes about your rate, equipment, or service..."
                      className="mt-2 min-h-[92px] resize-none border-[#E5E9EC] text-[13px]"
                    />
                    <div className="mt-1 text-right text-[10px] text-[#8A9299]">{notes.length} / 200</div>
                  </div>

                  <ul className="mt-3 space-y-2">
                    {[
                      "Rate is all-in & includes fuel",
                      "You have capacity & equipment available",
                      "You can meet the required pickup window",
                      "You agree to work directly with the shipper",
                    ].map((t) => (
                      <li key={t} className="flex items-start gap-2 text-[12px] text-[#374151]">
                        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#02857E]" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>

                  {isLtlLoad(load.load_type) && (
                    <label className="mt-3 flex min-h-[44px] cursor-pointer items-center gap-2 rounded-md border border-amber-500 bg-amber-50 px-2.5 py-2">
                      <input
                        type="checkbox"
                        checked={ltlAck}
                        onChange={(e) => setLtlAck(e.target.checked)}
                        className="h-4 w-4 shrink-0 accent-amber-600"
                      />
                      <span className="text-[11px] font-semibold leading-snug text-amber-900">
                        Confirm you are submitting a quote for an LTL load.
                      </span>
                    </label>
                  )}
                  <Button
                    onClick={onSubmit}
                    disabled={!canSubmit || submitting || !rate || (isLtlLoad(load.load_type) && !ltlAck)}
                    className="mt-4 h-12 w-full bg-[#02645E] text-sm font-bold uppercase tracking-wide text-white hover:bg-[#02514D]"
                  >
                    {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                    Submit quote
                  </Button>
                  {isLtlLoad(load.load_type) && !ltlAck && (
                    <p className="mt-2 text-center text-[11px] font-semibold text-amber-700">
                      Check the LTL confirmation above to enable submit.
                    </p>
                  )}
                  <p className="mt-2 text-center text-[11px] text-[#8A9299]">
                    Quotes are confidential until the deadline
                  </p>
                </>
              )}
            </div>
          </div>

          {/* QUOTE ACTIVITY */}
          <div className={cn(CARD, "overflow-hidden")}>
            <div className="border-b border-[#E5E9EC] px-4 py-3">
              <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#4B5563]">
                Quote activity
              </span>
            </div>
            <div className="divide-y divide-[#F1F4F5]">
              <ActivityRow
                icon={<FileText className="h-4 w-4 text-[#7E22CE]" />}
                ring="ring-[#EBD9FB] bg-[#F9F2FF]"
                title="Quote Request Posted"
                body={`${requestedBy ?? "The shipper"} posted this quote request`}
                at={postedAt}
              />
              <ActivityRow
                icon={<ShieldCheck className="h-4 w-4 text-[#02857E]" />}
                ring="ring-[#D3EAE6] bg-[#F0F7F5]"
                title="Deadline Set"
                body={load.bid_deadline ? `Quotes due by ${format(new Date(load.bid_deadline), "MMM d, yyyy h:mm a")}` : "No deadline set"}
                at={postedAt}
              />
              <ActivityRow
                icon={<Bell className="h-4 w-4 text-[#2563EB]" />}
                ring="ring-[#D8E4FD] bg-[#F1F6FE]"
                title="Notifications Sent"
                body={notifiedCount != null ? `Sent to ${notifiedCount} brokers in your network` : "—"}
                at={postedAt}
              />
              {existingQuote && (
                <ActivityRow
                  icon={<Send className="h-4 w-4 text-[#02857E]" />}
                  ring="ring-[#D3EAE6] bg-[#F0F7F5]"
                  title="Your Quote Submitted"
                  body={`${money(existingQuote.rate)} all-in`}
                  at={existingQuote.created_at}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Sub-parts ───────────────────────────────────────────── */

function StopList({
  title, count, icon, iconBg, stops, loading, fallback,
}: {
  title: string;
  count: number;
  icon: React.ReactNode;
  iconBg: string;
  stops: QuoteStop[];
  loading: boolean;
  fallback: { city: string; state: string; date: string | null };
}) {
  const rows = stops.length
    ? stops.map((s, i) => ({ n: i + 1, city: s.city, state: s.state, date: s.scheduled_date }))
    : [{ n: 1, city: fallback.city, state: fallback.state, date: fallback.date }];

  return (
    <div className="min-w-0">
      <div className="mb-3 flex items-center gap-2">
        <span className={cn("flex h-6 w-6 items-center justify-center rounded-full", iconBg)}>{icon}</span>
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#4B5563]">{title}</span>
        <span className="text-[11px] text-[#8A9299]">
          {loading ? "…" : `${count || 1} Stop${(count || 1) === 1 ? "" : "s"}`}
        </span>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.n} className="flex items-center gap-2.5">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F1F5F5] text-[10px] font-bold text-[#4B5563]">
              {r.n}
            </span>
            <span className="min-w-0 flex-1 truncate text-[13px] text-[#111827]">
              {r.city}, {r.state}
            </span>
            <span className="shrink-0 text-[12px] text-[#8A9299]">
              {r.date ? format(new Date(`${r.date}T12:00:00`), "MMM d") : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SpecRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#8A9299]">{label}</span>
      <span className="truncate text-right text-[13px] font-medium text-[#111827]">{value}</span>
    </div>
  );
}

function InsightTile({ label, value, bg, iconClass }: { label: string; value: string; bg: string; iconClass: string }) {
  return (
    <div className={cn("rounded-lg border border-[#E5E9EC] p-3", bg)}>
      <div className="flex items-start justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">{label}</span>
        <BarChart3 className={cn("h-3.5 w-3.5", iconClass)} />
      </div>
      <div className="mt-2 text-xl font-bold tabular-nums text-[#111827]">{value}</div>
    </div>
  );
}

function ActivityRow({
  icon, ring, title, body, at,
}: { icon: React.ReactNode; ring: string; title: string; body: string; at: string | null }) {
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1", ring)}>{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold text-[#111827]">{title}</div>
        <div className="truncate text-[11px] text-[#8A9299]">{body}</div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-[11px] text-[#6B7280]">{at ? format(new Date(at), "MMM d") : "—"}</div>
        <div className="flex items-center justify-end gap-1 text-[10px] text-[#9AA1A8]">
          <Clock className="h-2.5 w-2.5" />
          {at ? format(new Date(at), "h:mm a") : "—"}
        </div>
      </div>
    </div>
  );
}
